import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { SimulationBoard } from "./SimulationBoard";
import { lt } from "../localize";
import type { LessonStage } from "../types";

interface StageDecisionProps {
  stage: LessonStage;
}

const OPTION_STYLES = [
  // First option (Fold) — bold red
  {
    base: "bg-destructive/20 border-destructive/50 text-destructive hover:bg-destructive/30",
    icon: "text-destructive",
  },
  // Second option (Call) — calm blue
  {
    base: "bg-blue-500/20 border-blue-500/50 text-blue-400 hover:bg-blue-500/30",
    icon: "text-blue-400",
  },
  // Third option (Raise) — golden aggressive
  {
    base: "bg-secondary/20 border-secondary/50 text-secondary hover:bg-secondary/30",
    icon: "text-secondary",
  },
];

export const StageDecision = ({ stage }: StageDecisionProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const decision = stage.decision;
  const [selected, setSelected] = useState<number | null>(null);

  if (!decision) return null;

  const answered = selected !== null;
  const isCorrect = answered && decision.options[selected].isCorrect;

  const handleSelect = (i: number) => {
    if (answered) return;
    setSelected(i);
  };

  return (
    <div className="space-y-4">
      {/* Scenario */}
      <SimulationBoard simulation={decision.scenario} />

      {/* Prompt */}
      <p className="font-bold text-base text-center">
        {lt(decision.prompt, lang)}
      </p>

      {/* "Your play" badge */}
      {!answered && (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.06] border border-white/[0.12] text-xs font-medium text-muted-foreground backdrop-blur-sm">
            <span className="text-secondary">&#9824;</span>
            Tu jugada
          </span>
        </div>
      )}

      {/* Action buttons */}
      <div className="grid grid-cols-3 gap-2">
        {decision.options.map((opt, i) => {
          const isSelected = selected === i;
          const showCorrect = answered && opt.isCorrect;
          const showWrong = answered && isSelected && !opt.isCorrect;
          const style = OPTION_STYLES[i] ?? OPTION_STYLES[2];

          return (
            <button
              key={i}
              onClick={() => handleSelect(i)}
              disabled={answered}
              className={cn(
                "relative py-5 px-3 rounded-xl border text-center transition-all duration-200 font-bold text-base",
                !answered && style.base,
                !answered && "cursor-pointer active:scale-95",
                answered && "cursor-default",
                showCorrect && "!border-primary !bg-primary/20",
                showWrong && "!border-destructive !bg-destructive/20",
                answered && !showCorrect && !showWrong && "opacity-30",
              )}
              style={
                showCorrect
                  ? { animation: "correctPulse 0.6s ease-out" }
                  : showWrong
                    ? { animation: "wrongShake 0.4s ease-out" }
                    : undefined
              }
            >
              <div className="flex flex-col items-center gap-1.5">
                {showCorrect && <CheckCircle2 className="w-5 h-5 text-primary" />}
                {showWrong && <XCircle className="w-5 h-5 text-destructive" />}
                <span>{lt(opt.text, lang)}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Feedback — slides up from bottom */}
      {answered && (
        <div
          className={cn(
            "glass p-4 text-sm leading-relaxed border-l-4",
            isCorrect ? "border-l-primary" : "border-l-destructive",
          )}
          style={{ animation: "slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards" }}
        >
          {isCorrect
            ? lt(decision.correctExplanation, lang)
            : lt(decision.wrongExplanation, lang)}
        </div>
      )}
    </div>
  );
};
